import React from 'react';
import { Link } from 'react-router-dom';
import Spacing from 'components/Spacing';
import PageSection from 'components/PageSection';
import GridContainer from 'components/GridContainer';
import {
  CalendarDay,
  dateToLabel,
  numberToCurrencyString,
} from 'further-ui/utils';
import LabelValueRow from '../shared/LabelValueRow';
import { Grid, Tooltip, styled, Box } from '@mui/material';
import { AppDatePicker } from 'components/FormElements';
import { FundAllocation } from '../index';
import { UseFormSetValue } from 'react-hook-form';
import { InfoOutlined } from '@mui/icons-material';
import { Row } from 'components/Layout';
import { createExitEditRoute } from 'adminConstants/routes';

type FormValues = {
  fundAllocations: Array<FundAllocation>;
};

const StyledInfoIcon = styled(InfoOutlined)(({ theme }) => ({
  fontSize: '16px',
  marginLeft: theme.spacing(1),
  cursor: 'pointer',
  color: theme.palette.text.secondary,
}));

const StyledExitLink = styled(Link)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.text.green,
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

type CompanyDetailsProps = {
  investee?: {
    legalName?: string | null;
    tradingName?: string | null;
    shareClass?: string | null;
    nominalShareValue?: string | number | null;
  };
  dealSharePrice?: number;
  allocationDate?: CalendarDay;
  originalAllocationDate?: CalendarDay;
  isEditing?: boolean;
  setValue?: UseFormSetValue<FormValues>;
  formFundAllocations?: Array<FundAllocation>;
  containsFrozenInvestments?: boolean;
  isShareConsiderationAllocation?: boolean;
  exitedCompanyInfo?: {
    tradingName: string;
    shareClass: string;
    exitId: string;
    exitAmendmentId?: string;
  } | null;
};

const CompanyDetails: React.FC<CompanyDetailsProps> = ({
  investee,
  dealSharePrice,
  allocationDate,
  originalAllocationDate,
  isEditing = false,
  setValue,
  formFundAllocations,
  containsFrozenInvestments = false,
  isShareConsiderationAllocation = false,
  exitedCompanyInfo = null,
}) => {
  // Define the company detail fields (excluding investment date if editing)
  const companyDetails: Array<{
    label: string;
    value?: string | number | null | undefined;
    render?: () => React.ReactNode;
  }> = [
    { label: 'Legal name', value: investee?.legalName || '-' },
    { label: 'Trading name', value: investee?.tradingName || '-' },
    { label: 'Share class', value: investee?.shareClass || '-' },
    {
      label: 'Share price',
      value: numberToCurrencyString(dealSharePrice, {
        unlimitedDp: true,
      }),
    },
    { label: 'Nominal share value', value: investee?.nominalShareValue || '-' },
    // Only include investment date as a static field if not editing OR if contains frozen investments
    ...(!isEditing || containsFrozenInvestments
      ? [
          {
            label: 'Investment date',
            value: allocationDate?.toLabel(),
          },
        ]
      : []),
    // Include exited shareholdings field if this is a share consideration allocation
    ...(isShareConsiderationAllocation && exitedCompanyInfo
      ? [
          {
            label: 'Exited Shareholdings',
            render: () => {
              const exitRoute = createExitEditRoute(exitedCompanyInfo.exitId);
              const exitLink = exitedCompanyInfo.exitAmendmentId
                ? `${exitRoute}?exitAmendmentId=${exitedCompanyInfo.exitAmendmentId}`
                : exitRoute;

              return (
                <StyledExitLink to={exitLink}>
                  {`${exitedCompanyInfo.tradingName} (${exitedCompanyInfo.shareClass})`}
                </StyledExitLink>
              );
            },
          },
        ]
      : []),
  ];

  const handleInvestmentDateChange = (value: Date | null) => {
    if (!value || !setValue || !formFundAllocations) return;

    // Update allocationDate for all fund allocations
    formFundAllocations.forEach((_, index) => {
      setValue(
        `fundAllocations.${index}.allocationDate`,
        new CalendarDay(value),
      );
    });
  };

  return (
    <PageSection title="Company details">
      <GridContainer className="mt-2">
        <Grid
          size={{
            md: 5,
            sm: 12,
            xs: 12,
          }}
        >
          <Spacing size="sm">
            {companyDetails.map((detail) => (
              <LabelValueRow key={detail.label} label={detail.label}>
                {detail.render ? detail.render() : detail.value ?? '-'}
              </LabelValueRow>
            ))}
            {isEditing && !containsFrozenInvestments && (
              <Row spacing="lg" justify="space-between">
                <Box display="flex" alignItems="center">
                  Investment date
                  <Tooltip
                    arrow
                    title={`The allocation date must be the original allocation date (${dateToLabel(
                      originalAllocationDate?.toUTCDate(),
                    )}) or later (up to today's date). If you wish to select an earlier date, please delete this allocation and start again.`}
                  >
                    <StyledInfoIcon />
                  </Tooltip>
                </Box>
                <Box>
                  <AppDatePicker
                    value={allocationDate?.toUTCDate() ?? null}
                    onChange={handleInvestmentDateChange}
                    required
                    fullWidth
                    disableFuture
                    minDate={originalAllocationDate?.toUTCDate() ?? undefined}
                    placeholder="Enter investment date"
                  />
                </Box>
              </Row>
            )}
          </Spacing>
        </Grid>
      </GridContainer>
    </PageSection>
  );
};

export default CompanyDetails;
